import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import AuthLayout from './AuthLayout';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { supabase } from '../lib/supabase';

// US 10-digit OR E.164. Normalize by stripping non-digits then adding
// +1 for a 10-digit US number.
function normalizePhone(raw) {
  const digits = String(raw || '').replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  if (digits.length >= 10 && digits.length <= 15) return `+${digits}`;
  return null;
}

// Fire-and-forget phone handoff to sfa-admin (via server-side proxy
// so AGENT_TOOL_SECRET never touches the browser).
async function fireHandoff(phone, email) {
  try {
    const token = readPersistedJwtDirect();
    if (!token) return; // signup must have completed for this to fire
    await fetch('/api/signup/phone-handoff', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ phone, email }),
    });
  } catch { /* non-blocking */ }
}

// Best-effort local persistence of the signup business info + mobile
// onto the operator's subscriptions row. The auth-user metadata
// (options.data on signUp) is the reliable channel — the Stripe
// webhook backfills the row from it whichever way the two events
// order. This update is a belt-and-suspenders write for the
// pay-then-signup case where the subscriptions row ALREADY exists at
// signup time, so the values land the moment the account is created
// instead of waiting on the next webhook. Fire-and-forget: a failure
// (no session yet, RLS, no row) must never block signup completion —
// same philosophy as the phone handoff above.
async function persistBusinessInfoLocally(userId, patch) {
  if (!userId || !patch || Object.keys(patch).length === 0) return;
  try {
    await supabase.from('subscriptions').update(patch).eq('owner_id', userId);
  } catch { /* non-blocking */ }
}

export default function SignupScreen({ onSwitchToLogin }) {
  const { signUp } = useAuth();
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [businessAddress, setBusinessAddress] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);

  const submit = async () => {
    if (pending) return;
    setError('');
    const normalizedPhone = normalizePhone(phone);
    if (!normalizedPhone) {
      setError('Enter a valid US phone number (10 digits) or an international number in E.164 format.');
      return;
    }
    const bizName = businessName.trim();
    const bizAddress = businessAddress.trim();
    if (!bizName) {
      setError('Enter your business name — it registers your business phone line.');
      return;
    }
    if (!bizAddress) {
      setError('Enter your business address — it registers your business phone line.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setPending(true);
    // Stamp business name/address + mobile into the auth user metadata so
    // the Stripe webhook can backfill the subscriptions row regardless of
    // event ordering (signup-then-pay OR pay-then-signup).
    const result = await signUp(email.trim(), password, {
      business_name:    bizName,
      business_address: bizAddress,
      signup_phone:     normalizedPhone,
    });
    setPending(false);
    if (!result.ok) {
      setError(result.error || 'Could not create account.');
      return;
    }
    // Belt-and-suspenders: if the subscriptions row already exists
    // (pay-then-signup), write the same values straight onto it now.
    // Fire-and-forget — never block signup.
    persistBusinessInfoLocally(result.user?.id, {
      business_name:     bizName,
      business_address:  bizAddress,
      owner_mobile_e164: normalizedPhone,
    });
    // Phone handoff to sfa-admin. Fire-and-forget — never block signup
    // on cross-app latency. The endpoint is idempotent; retries are safe.
    fireHandoff(normalizedPhone, email.trim());
    // Email confirmation is off, so onAuthStateChange will fire with the
    // new session and the auth gate takes us into the app automatically.
  };

  const onKey = (e) => { if (e.key === 'Enter') submit(); };
  const canSubmit = !!email.trim() && !!phone.trim() && !!businessName.trim()
    && !!businessAddress.trim() && !!password && !!confirm && !pending;

  return (
    <AuthLayout
      eyebrow="Create your account"
      heading="Get started"
      subheading="One workspace. Every carrier. Set up in two minutes."
      footer={
        <>
          Already have an account?{' '}
          <button
            onClick={onSwitchToLogin}
            className="font-semibold text-amber-700 hover:text-amber-800 hover:underline"
          >
            Sign in →
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <label className="block">
          <span className="text-xs uppercase tracking-wider font-semibold text-slate-600">Email</span>
          <input
            type="email"
            autoComplete="email"
            value={email}
            onChange={e => { setEmail(e.target.value); setError(''); }}
            onKeyDown={onKey}
            placeholder="you@example.com"
            autoFocus
            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100 transition"
          />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wider font-semibold text-slate-600">Mobile phone</span>
          <input
            type="tel"
            autoComplete="tel"
            value={phone}
            onChange={e => { setPhone(e.target.value); setError(''); }}
            onKeyDown={onKey}
            placeholder="(555) 123-4567"
            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100 transition"
          />
          <span className="block text-[11px] text-slate-400 mt-1">
            Used for setup texts, activation nudges, and account recovery. US 10-digit or +country code.
          </span>
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wider font-semibold text-slate-600">Business name</span>
          <input
            type="text"
            autoComplete="organization"
            value={businessName}
            onChange={e => { setBusinessName(e.target.value); setError(''); }}
            onKeyDown={onKey}
            placeholder="Curry Coverage LLC"
            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100 transition"
          />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wider font-semibold text-slate-600">Business address</span>
          <input
            type="text"
            autoComplete="street-address"
            value={businessAddress}
            onChange={e => { setBusinessAddress(e.target.value); setError(''); }}
            onKeyDown={onKey}
            placeholder="123 Main St, Austin, TX 78701"
            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100 transition"
          />
          <span className="block text-[11px] text-slate-400 mt-1">
            Street, city, state, ZIP — used to register your business phone line.
          </span>
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wider font-semibold text-slate-600">Password</span>
          <input
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={e => { setPassword(e.target.value); setError(''); }}
            onKeyDown={onKey}
            placeholder="At least 6 characters"
            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100 transition"
          />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wider font-semibold text-slate-600">Confirm password</span>
          <input
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={e => { setConfirm(e.target.value); setError(''); }}
            onKeyDown={onKey}
            placeholder="Type it again"
            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100 transition"
          />
        </label>
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2.5 text-sm flex items-start gap-2">
            <span aria-hidden="true">⚠</span>
            <span>{error}</span>
          </div>
        )}
        <button
          onClick={submit}
          disabled={!canSubmit}
          className="w-full bg-amber-500 hover:bg-amber-600 active:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed text-slate-900 font-bold py-3 rounded-lg shadow-sm transition"
        >
          {pending ? 'Creating account…' : 'Create account'}
        </button>
      </div>
    </AuthLayout>
  );
}
